import { useCallback, useEffect, useRef, type RefObject } from 'react';

/** Within this many pixels of the bottom still counts as "at the bottom". */
const PINNED_SLACK_PX = 80;

/**
 * Keeps a chat scroller on its newest message as the thread grows - unless the rep has scrolled
 * up to read something earlier, which they keep. The first fill lands at the bottom instantly,
 * later growth scrolls smoothly. Shared by the AI Workspace chat and Prospecting › Search
 * (WEB-15681).
 *
 * Scrolls the scroller itself: scrollIntoView on a sentinel also scrolls every scrollable
 * ancestor, permanently shifting the embeds' clipped boxes.
 *
 * `watched` is what makes the thread grow (messages, streamed text and thinking); the scroller
 * must be mounted on the first render.
 */
export function useStickToBottom(scrollerRef: RefObject<HTMLElement | null>, watched: readonly unknown[]) {
	const pinnedRef = useRef(true);
	const initialLoadRef = useRef(true);

	useEffect(() => {
		const el = scrollerRef.current;
		if (!el) return;
		const onScroll = () => {
			pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= PINNED_SLACK_PX;
		};
		el.addEventListener('scroll', onScroll, { passive: true });
		return () => el.removeEventListener('scroll', onScroll);
	}, [scrollerRef]);

	useEffect(() => {
		if (!initialLoadRef.current && !pinnedRef.current) return;
		const el = scrollerRef.current;
		el?.scrollTo({ top: el.scrollHeight, behavior: initialLoadRef.current ? 'instant' : 'smooth' });
		initialLoadRef.current = false;
		// The caller decides what growth means; spreading it is the point.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scrollerRef, ...watched]);

	/** The next growth is a conversation loading, so it jumps to the bottom whatever the scroll. */
	return useCallback(() => {
		initialLoadRef.current = true;
	}, []);
}
